import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';

import {
  Agencies,
  getPharmacistHistory,
  getPharmacyHistory,
  pharmacistActions,
  pharmacyActions,
  professionalActions,
  resetStatus,
  successOrSelector,
  ToUserTypings,
  TypeOfUser,
  userConfigurations,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';
import DynamicTable from '../../DynamicTable';
import AdsContainer from '../../common/AdsContainer';
import OutcomeModal from '../../Modals/OutcomeModal';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import useUser from '../../../hooks/useUser';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  nameLabels,
  NavType,
  OutcomeModalTypes,
} from '../../../helpers/Constants';
import ResponsiveClasses from '../../../theme/ResponsiveClasses';
import { setDialog } from '../../../reducers/dialogReducer';
import strings from '../../../localization';

import { pharmacyHistoryMapper, pharmacistHistoryMapper } from '../helpers';
import PharmacistRating from '../PharmacistRating/PharmacistRating';
import { showError } from '../../Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import useSelfService from '../../../hooks/useSelfService';
import useProfessional from '../../../hooks/Professional/useProfessional';

const History = () => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const configurations = useAppSelector(userConfigurations);
  const { isSelfService } = useSelfService();

  const { state } = useLocation();
  const { id, name, source } = state ?? {};
  const { history, userType } = useUser();
  const { data, totalCount } = history;
  const { isProfessional } = useProfessional();
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const isAutoSync = source === Agencies.PharmaPlan && isSelfService;

  const loadSuccess = useAppSelector((st) =>
    successOrSelector([pharmacistActions.history, pharmacyActions.history], st));

  const ratingSuccess = useAppSelector((st) =>
    successOrSelector([pharmacyActions.rate, professionalActions.rate], st));

  const label = isProfessional ? strings.pharmacy : nameLabels().roleLabel;

  const api = (inId: string, inPage: number) => {
    if (isProfessional) {
      return dispatch(
        getPharmacistHistory({
          pharmacyId: inId,
          paging: { page: inPage },
          source,
        }),
      );
    }
    return dispatch(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: THIS IS FOR DYNAMIC
      getPharmacyHistory({
        source,
        [idStore.id]: inId,
        paging: { page: inPage },
      }),
    );
  };

  const OpenModal = (bookingId: string, canRate: boolean) => {
    if (!canRate) {
      showError(
        dispatch,
        strings.formatString(
          strings.ratingAPharmacistFormat,
          configurations.numberOfDaysToRatePharmacist,
        ) as string,
      );
      return;
    }

    dispatch(
      setDialog({
        showCloseButton: false,
        Component: (
          <PharmacistRating
            pharmacistId={id}
            pharmacistName={name}
            bookingId={bookingId}
          />
        ),
        heading: {
          title: '',
        },
        maxWidth: 'sm',
      }),
    );
  };

  const professionalConfig = {
    headers: [
      { key: '0', label: '#' },
      { key: '1', label: strings.shiftCode },
      { key: '2', label: strings.date },
      { key: '3', label: strings.time },
      isSelfService
        && !isAutoSync && {
        key: '4',
        label: strings.rate,
        isCenterAligned: true,
      },
    ].filter(Boolean),
    rows: pharmacistHistoryMapper(data, timeFormat, OpenModal, page),
  };

  const userConfig = ToUserTypings({
    [TypeOfUser.pharmacy]: {
      headers: [
        { key: '0', label: '#' },
        { key: '1', label: strings.shiftCode },
        { key: '2', label: strings.date },
        { key: '3', label: strings.time },
        { key: '6', label: strings.action, isCenterAligned: true },
      ],
      rows: pharmacyHistoryMapper(data, timeFormat, OpenModal, page),
    },
    [TypeOfUser.pharmacist]: professionalConfig,
    [TypeOfUser.student]: professionalConfig,
    [TypeOfUser.technician]: professionalConfig,
  });

  const Table = {
    title: `${strings.formatString(strings.myUserHistory, label)} [${name}]`,

    headers: userConfig[userType].headers,
    headerBar: [
      {
        Component: <div />,
        key: '1',
      },
    ],
    rows: userConfig[userType].rows,
  };

  useEffect(() => {
    api(id, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ratingSuccess) {
      dispatch(
        setDialog({
          Component: (
            <OutcomeModal
              type={OutcomeModalTypes.submitSuccess}
              message={strings.ratingSuccessful}
            />
          ),
          heading: { title: '' },
          showCloseButton: true,
        }),
      );
      dispatch(resetStatus([pharmacyActions.rate]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingSuccess]);

  const handlePagination = (_: unknown, selectedPage: number) => {
    const goToPage = selectedPage + 1;

    setPage(goToPage);
    api(id, goToPage);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={10}>
        <DynamicTable
          table={Table}
          loadSuccess={loadSuccess}
          totalCount={totalCount}
          page={page}
          type={NavType.history}
          handlePagination={handlePagination}
        />
      </Grid>
      <Grid item xs={2} sx={ResponsiveClasses.hideOnLg}>
        <AdsContainer />
      </Grid>
    </Grid>
  );
};

export default History;
